import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WalletCards, Landmark, AlertTriangle, RefreshCw } from 'lucide-react';
import { shortDate } from '@/lib/utils';
import {
  getCreditHirePayments,
  getCreditHireStats,
  type CreditHirePaymentRow,
} from '@/lib/workerApi';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DataPagination } from '@/components/DataPagination';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

// cr_hires.status lifecycle (see 20260417213638_create_brand_hiring_v1.sql).
// 'declined'/'expired_offer' dropped as tabs — pre-charge terminal states
// (or, for a released waitlisted hire, one whose money was already refunded)
// that aren't worth a dedicated filter; a row in either state still shows up
// under "All" if one exists. 'failed' stays as a tab (still worth finding
// one if it exists) but is excluded from the stat cards below — see
// CREDIT_HIRE_SUCCESSFUL_STATUSES in the worker.
type StatusTab = 'all' | 'confirmed' | 'completed' | 'failed' | 'waitlisted';
const STATUS_TABS: { key: StatusTab; label: string }[] = [
  { key: 'all',        label: 'All' },
  { key: 'confirmed',  label: 'Confirmed' },
  { key: 'completed',  label: 'Completed' },
  { key: 'failed',     label: 'Failed' },
  { key: 'waitlisted', label: 'Waitlisted' },
];

function usd(cents: number | null | undefined) {
  if (cents === null || cents === undefined) return null;
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function HireStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-xs text-muted-foreground">—</span>;
  const cls =
    status === 'completed'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : status === 'confirmed'
        ? 'bg-blue-100 text-blue-800 border-blue-200'
        : status === 'failed' || status === 'declined' || status === 'expired_offer'
          ? 'bg-red-100 text-red-800 border-red-200'
          : 'bg-slate-100 text-slate-700 border-slate-200';
  return <Badge className={`border ${cls} text-[10px] px-1.5 py-0 capitalize`}>{status.replace(/_/g, ' ')}</Badge>;
}

/** 'credit' = the whole hire came out of the wallet, never touched Whop.
 *  'mixed' = credit covered part of it, the rest was charged to a card (and
 *  that card portion also shows up on the Whop Hire Payments tab). */
function FundingBadge({ source }: { source: CreditHirePaymentRow['funding_source'] }) {
  if (source === 'mixed') {
    return <Badge className="bg-purple-100 text-purple-800 border border-purple-200 text-[10px] px-1.5 py-0">Credit + Card</Badge>;
  }
  return <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-200 text-[10px] px-1.5 py-0">Credit only</Badge>;
}

function CreatorCell({ row }: { row: CreditHirePaymentRow }) {
  if (!row.creator_name) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <div>
      <div className="font-medium truncate">{row.creator_name}</div>
      {row.creator_tiktok_username && (
        <div className="text-[11px] text-muted-foreground">@{row.creator_tiktok_username}</div>
      )}
    </div>
  );
}

type StatAccent = 'emerald' | 'cyan' | 'amber' | 'purple';

// Same shape/pattern as the other finance panels' STAT_ACCENTS — kept local
// (see their comments for why). Cyan/amber pair the same way Gross Hire
// Value's segments do: cyan is the credit-funded portion, amber the
// card-charged portion; purple is BMC's own cut (Platform Fee), kept visually
// distinct from both so it doesn't read as a third slice of the same split.
const STAT_ACCENTS: Record<StatAccent, { tile: string; value: string; glow: string; pill: string; bar: string }> = {
  emerald: {
    tile: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    value: 'text-emerald-600 dark:text-emerald-400',
    glow: 'from-emerald-500/[0.12]',
    pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    bar: 'bg-emerald-500',
  },
  cyan: {
    tile: 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400',
    value: 'text-foreground',
    glow: 'from-cyan-500/[0.12]',
    pill: 'bg-cyan-100 text-cyan-700 border border-cyan-200',
    bar: 'bg-cyan-500',
  },
  amber: {
    tile: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
    value: 'text-foreground',
    glow: 'from-amber-500/[0.12]',
    pill: 'bg-amber-100 text-amber-700 border border-amber-200',
    bar: 'bg-amber-500',
  },
  purple: {
    tile: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    value: 'text-purple-600 dark:text-purple-400',
    glow: 'from-purple-500/[0.12]',
    pill: 'bg-purple-100 text-purple-700 border border-purple-200',
    bar: 'bg-purple-500',
  },
};

/**
 * Premium tile for the four totals — same rounded-xl + corner-glow treatment
 * as the other three finance panels' StatCard. The successful-hire count is a
 * colored pill instead of plain caption text. No truncation state here — this
 * reads straight from our own DB, not a capped live Whop scan.
 */
function StatCard({ icon: Icon, label, value, count, countLabel = 'successful', sub, accent, segments, loading }: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  count?: number;
  countLabel?: string;
  sub?: React.ReactNode;
  accent: StatAccent;
  /** This card's share of Gross Hire Value, as filled segments (0-100 each)
   *  under the pill row. */
  segments?: { pct: number; className: string }[];
  loading?: boolean;
}) {
  const a = STAT_ACCENTS[accent];
  return (
    <div className="group relative overflow-hidden rounded-xl border bg-card p-5 shadow-sm transition-shadow duration-200 hover:shadow-elevate-lg">
      <div className={`pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full bg-gradient-to-br ${a.glow} to-transparent blur-2xl transition-opacity duration-200 group-hover:opacity-125`} />
      <div className="relative space-y-3">
        <div className="flex items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${a.tile}`}>
            <Icon className="h-4 w-4" />
          </span>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>

        {loading ? (
          <Skeleton className="h-9 w-32" />
        ) : (
          <p className={`text-3xl font-bold leading-none tracking-tight tabular-nums ${a.value}`}>{value}</p>
        )}

        <div className="flex min-h-[22px] flex-wrap items-center gap-2">
          {loading ? (
            <Skeleton className="h-[22px] w-24 rounded-full" />
          ) : (
            <>
              {count !== undefined && (
                <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold tabular-nums ${a.pill}`}>
                  {count.toLocaleString()} {countLabel}
                </span>
              )}
              {sub && <span className="text-xs text-muted-foreground">{sub}</span>}
            </>
          )}
        </div>

        {!loading && segments && segments.length > 0 && (
          <div className="flex h-1 w-full overflow-hidden rounded-full bg-muted">
            {segments.map((s, i) => (
              <div
                key={i}
                className={`h-full transition-all duration-500 ${s.className}`}
                style={{ width: `${Math.max(0, Math.min(s.pct, 100))}%` }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Hires funded (fully or partly) by wallet credit — read straight from
 * cr_hires, no Whop call. This is the ONLY finance tab that can see a hire
 * fully covered by wallet credit: chargeForHire returns before ever calling
 * Whop when credit covers the whole amount, so that hire never creates a
 * Whop payment and is invisible to the two Whop-sourced tabs. A "mixed" hire
 * (credit + card) also shows a row on the Whop Hire Payments tab, but only
 * for the card-charged remainder — this tab is the only place its full value
 * and the credit portion are visible together.
 */
export default function CreditHiresPanel() {
  const [statusTab, setStatusTab] = useState<StatusTab>('all');
  const [page, setPage]           = useState(1);
  const [pageSize, setPageSize]   = useState(25);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['credit-hire-payments', statusTab, page, pageSize],
    queryFn: () => getCreditHirePayments({
      limit: pageSize,
      offset: (page - 1) * pageSize,
      statuses: statusTab === 'all' ? undefined : [statusTab],
    }),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  // Cheap (one DB query, no enrichment) — no need for the Whop hire tab's
  // aggressive 5-minute staleTime, but still no reason to refetch on every
  // tab switch either.
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['credit-hire-stats'],
    queryFn: () => getCreditHireStats(),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const rows       = data?.rows ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const statusCounts = statsData?.status_counts ?? {};
  const fundingCounts = statsData?.funding_counts;

  // Credit vs card's share of Gross Hire Value, and the credit-only platform
  // fee's share of it too — for the stat cards' proportion bars. Zero (not
  // NaN) while stats haven't loaded or nothing's been collected yet.
  const grossCents           = statsData?.stats.total_brand_rate_cents ?? 0;
  const creditCents          = statsData?.stats.total_credit_applied_cents ?? 0;
  const cardCents            = statsData?.stats.total_card_charged_cents ?? 0;
  const platformCreditCents  = statsData?.stats.total_platform_fee_credit_only_cents ?? 0;
  const platformMixedCents   = statsData?.stats.total_platform_fee_mixed_gross_cents ?? 0;
  const creditShare          = grossCents > 0 ? (creditCents / grossCents) * 100 : 0;
  const cardShare            = grossCents > 0 ? (cardCents / grossCents) * 100 : 0;
  const platformCreditShare  = grossCents > 0 ? (platformCreditCents / grossCents) * 100 : 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Hires paid fully or partly with wallet credit, read straight from our own database — a hire
          fully covered by credit never creates a Whop payment, so it never appears on the Hiring
          Transactions tab. "Credit + Card" rows also have a partial charge there for the card portion.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-3.5 w-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          {isFetching ? 'Refreshing' : 'Refresh'}
        </Button>
      </div>

      {/* Every total below counts successful hires only ('confirmed' |
          'completed' | 'partial_completed') — see
          CREDIT_HIRE_SUCCESSFUL_STATUSES in the worker. 'failed' never
          contributes (no live path even sets it today); 'expired_offer' is
          excluded too, since a released waitlisted hire keeps its original
          brand_rate_cents/credit_applied_cents on the row even after the
          credit is refunded (refunded_credit_cents tracks the giveback
          separately, same as everywhere else this ledger never edits a
          charge after the fact). Gross Hire Value's bar splits into the
          credit-funded share (cyan) and the card-charged share (amber);
          Credit Applied and Card Charged each show their own share. */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard
          icon={WalletCards}
          label="Gross Hire Value"
          value={statsLoading ? '—' : usd(grossCents)}
          count={statsData?.stats.total_successful_count}
          sub={statsLoading || grossCents === 0 ? undefined : `${creditShare.toFixed(0)}% credit · ${cardShare.toFixed(0)}% card`}
          accent="emerald"
          segments={[
            { pct: creditShare, className: 'bg-cyan-500' },
            { pct: cardShare, className: 'bg-amber-500' },
          ]}
          loading={statsLoading}
        />
        <StatCard
          icon={WalletCards}
          label="Credit Applied"
          value={statsLoading ? '—' : usd(creditCents)}
          count={statsData?.stats.total_successful_count}
          sub="wallet credit actually spent"
          accent="cyan"
          segments={[{ pct: creditShare, className: 'bg-cyan-500' }]}
          loading={statsLoading}
        />
        <StatCard
          icon={WalletCards}
          label="Card Charged (mixed)"
          value={statsLoading ? '—' : usd(cardCents)}
          count={fundingCounts?.mixed}
          countLabel="mixed hires"
          // sub="remainder charged on mixed hires"
          accent="amber"
          segments={[{ pct: cardShare, className: 'bg-amber-500' }]}
          loading={statsLoading}
        />
        {/* Exact on the credit-only half — a hire that never reached Whop has
            no processing fee to net off, so BMC keeps the whole margin. The
            mixed half is disclosed separately below rather than folded in,
            since Whop's cut on those card charges isn't in our DB — so the
            pill/bar here describe the credit-only figure specifically, not
            every successful hire. */}
        <StatCard
          icon={Landmark}
          label="Platform Fee"
          value={statsLoading ? '—' : usd(platformCreditCents)}
          count={fundingCounts?.credit}
          countLabel="credit-only"
          // sub={
          //   statsLoading
          //     ? 'credit-only hires'
          //     : `exact${platformMixedCents > 0 ? ` · +${usd(platformMixedCents)} gross on mixed` : ''}`
          // }
          accent="purple"
          segments={[{ pct: platformCreditShare, className: 'bg-purple-500' }]}
          loading={statsLoading}
        />
      </div>

      {/* Same disclosure discipline as the Whop tab's fee-floor banners: say
          which half of the number is exact rather than presenting a blended
          total as realised revenue. */}
      {!statsLoading && (statsData?.stats.total_platform_fee_mixed_gross_cents ?? 0) > 0 && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-amber-800">
            The Platform Fee above counts credit-only hires, where it's exact — no Whop charge means
            no processing fee to deduct. The {fundingCounts?.mixed ?? 0} "Credit + Card" hire(s) add{' '}
            {usd(statsData?.stats.total_platform_fee_mixed_gross_cents)} <strong>gross</strong>: Whop's cut
            on their card portion lives on Whop, not in our database, so it isn't netted off here. See
            the Hiring Transactions tab for the realised figure on those.
          </p>
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="flex items-center gap-1 flex-wrap border-b px-4 pt-1 pb-1">
          {STATUS_TABS.map(({ key, label }) => {
            const count = key === 'all'
              ? statsData?.total_count ?? Object.values(statusCounts).reduce((s, n) => s + Number(n), 0)
              : Number(statusCounts[key] ?? 0);
            return (
              <button
                key={key}
                type="button"
                onClick={() => { setStatusTab(key); setPage(1); }}
                className={`px-3 py-2.5 text-sm font-medium transition-colors flex items-center gap-1.5 ${
                  statusTab === key
                    ? 'border-b-2 border-foreground text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
                <span className={`text-xs rounded-full px-1.5 py-0.5 tabular-nums ${statusTab === key ? 'bg-foreground/10 text-foreground' : 'bg-muted text-muted-foreground'}`}>
                  {statsLoading ? '…' : count}
                </span>
              </button>
            );
          })}
        </div>

        {error ? (
          <div className="flex flex-col items-center justify-center py-16 text-sm gap-3 px-6 text-center">
            <AlertTriangle className="h-10 w-10 text-red-400" />
            <p className="font-medium text-red-700">Could not load credit-funded hires.</p>
            <p className="text-muted-foreground max-w-md">{(error as Error).message}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>Try again</Button>
          </div>
        ) : isLoading ? (
          <div className="p-6 space-y-3">
            {Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
          </div>
        ) : totalItems === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground text-sm gap-2">
            <WalletCards className="h-10 w-10 opacity-25 mb-2" />
            <p className="font-medium">No credit-funded hires found.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">Brand</TableHead>
                    <TableHead className="whitespace-nowrap">Creator</TableHead>
                    <TableHead className="whitespace-nowrap">Campaign</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Package</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="whitespace-nowrap">Funding</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Hire Value</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Credit Applied</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Card Charged</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Platform Fee</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Refunded</TableHead>
                    <TableHead className="whitespace-nowrap">Charged</TableHead>
                    <TableHead className="whitespace-nowrap">Hire ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell className="max-w-[180px] truncate font-medium">
                        {row.brand_name ?? '—'}
                      </TableCell>
                      <TableCell className="max-w-[180px]"><CreatorCell row={row} /></TableCell>
                      <TableCell className="max-w-[180px]">
                        <div className="text-sm truncate">{row.campaign_name ?? '—'}</div>
                        {row.product_name && (
                          <div className="text-[11px] text-muted-foreground truncate">{row.product_name}</div>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{row.package_size ?? '—'}</TableCell>
                      <TableCell><HireStatusBadge status={row.status} /></TableCell>
                      <TableCell><FundingBadge source={row.funding_source} /></TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap font-medium">
                        {usd(row.brand_rate_cents) ?? '—'}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap text-emerald-700">
                        {usd(row.credit_applied_cents)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap text-muted-foreground text-sm">
                        {row.card_charged_cents > 0 ? usd(row.card_charged_cents) : '—'}
                      </TableCell>
                      {/* Exact on a credit-only hire; on a mixed one Whop's cut
                          of the card half isn't ours to see, so it's labelled
                          gross rather than shown as realised. */}
                      <TableCell className="text-right tabular-nums whitespace-nowrap">
                        <span className="font-medium text-emerald-700">{usd(row.platform_fee_cents)}</span>
                        {row.funding_source === 'mixed' && (
                          <span
                            className="ml-1 text-[10px] text-amber-700"
                            title="Gross — Whop's fee on this hire's card portion isn't deducted"
                          >
                            gross
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums whitespace-nowrap text-sm">
                        {row.refunded_credit_cents > 0 ? (
                          <span className="text-red-600">−{usd(row.refunded_credit_cents)}</span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {row.confirmed_at ? shortDate(row.confirmed_at) : shortDate(row.created_at)}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {row.id}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DataPagination
              currentPage={page}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          </>
        )}
      </div>
    </div>
  );
}
